import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Modal, Table, Tabs } from "antd";
import dayjs from "dayjs";
import http from "@/utils/http";
import api from "@/utils/api";

const dateCell = (value) => value && !String(value).startsWith("1000-")
  ? dayjs(value).format("MM/DD/YYYY") : "—";

export default function HistoryCheckModal({ socialSecurityNumbers, onClose, onReviewed }) {
  const [query, setQuery] = useState({ kind: "rental", page: 1, size: 10 });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const requestSequence = useRef(0);
  const [checked, setChecked] = useState({});

  useEffect(() => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    setFailed(false);
    setResult(null);
    setChecked((current) => ({ ...current, [query.kind]: false }));
    http.post(api.creditCheckHistory, { ...query, socialSecurityNumbers })
      .then((response) => {
        if (sequence !== requestSequence.current) return;
        if (response.code !== "1") throw new Error("History unavailable");
        setResult(response.data);
        setChecked((current) => ({ ...current, [query.kind]: true }));
      })
      .catch(() => { if (sequence === requestSequence.current) setFailed(true); })
      .finally(() => { if (sequence === requestSequence.current) setLoading(false); });
    return () => { requestSequence.current += 1; };
  }, [query, socialSecurityNumbers, retry]);

  const columns = [
    { title: "Prop", dataIndex: "prop", width: 90 },
    { title: "Unit", dataIndex: "unit", width: 80 },
    { title: "Tnt", dataIndex: "tenant", width: 70 },
    { title: "Move-in date", dataIndex: "moveInDate", render: dateCell, width: 130 },
    { title: "Move-out date", dataIndex: "moveOutDate", render: dateCell, width: 130 },
    ...(query.kind === "eviction" ? [
      { title: "Case number", dataIndex: "caseNumber", render: (value) => value || "—" },
      { title: "Last event", dataIndex: "lastEventStatus", render: (value) => value || "—" },
    ] : []),
  ];

  return <Modal open centered width={900} className="cc-detail-modal" title="History Check"
    maskClosable={false} onCancel={onClose} footer={<>
      <Button onClick={onClose}>Close</Button>
      <Button type="primary" disabled={loading || failed || !checked.rental || !checked.eviction}
        onClick={onReviewed}>I have reviewed both histories</Button>
    </>}>
    <p className="cc-history-description">Review both tabs before running credit. These records cover properties you can access.</p>
    <Tabs activeKey={query.kind} onChange={(kind) => setQuery((current) => ({ ...current, kind, page: 1 }))}
      items={[{ key: "rental", label: `1. Rental history${checked.rental ? " ✓" : ""}` },
        { key: "eviction", label: `2. Eviction information${checked.eviction ? " ✓" : ""}` }]} />
    {failed ? <Alert showIcon type="error" message="Could not load history. Please try again."
      action={<Button size="small" onClick={() => setRetry((value) => value + 1)}>Retry</Button>} />
      : <Table rowClassName="table-row-gradient" size="small" rowKey="historyId" columns={columns} dataSource={result?.list || []}
        loading={loading} scroll={{ x: query.kind === "eviction" ? 750 : 500 }}
        locale={{ emptyText: query.kind === "eviction" ? "No eviction records found" : "No rental records found" }}
        pagination={{ current: query.page, pageSize: query.size, total: result?.total || 0,
          showSizeChanger: true, onChange: (page, size) => setQuery((current) => ({ ...current, page, size })) }} />}
  </Modal>;
}
